/**
 * Cart Page Functionality
 * Handles cart quantity updates, item removal, and cart note updates
 */

(function() {
  'use strict';

  const lineLocks = new Set();
  let refreshController = null;
  let refreshRequestId = 0;
  let cartPageRetryHandler = null;
  const i18n = (window.SHOPIFY_CONFIG && window.SHOPIFY_CONFIG.translations) || {};
  const requestClient = window.themeRequest || {
    fetchJSON: function(url, options) {
      return fetch(url, options).then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      });
    },
    fetchText: function(url, options) {
      return fetch(url, options).then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      });
    }
  };

  function ensureErrorContainer() {
    const host = document.getElementById('cart-page-content');
    if (!host) return null;

    let container = document.getElementById('cart-page-error');
    if (container) return container;

    container = document.createElement('div');
    container.id = 'cart-page-error';
    container.className = 'hidden mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700';
    const retryLabel = i18n.retryLabel || 'Retry';
    container.innerHTML = `<span data-role="message"></span> <button type="button" class="ml-2 underline font-medium" data-role="retry">${retryLabel}</button>`;

    host.prepend(container);
    return container;
  }

  function showCartPageError(message, retryHandler) {
    const container = ensureErrorContainer();
    if (!container) return;
    const messageNode = container.querySelector('[data-role="message"]');
    const retryButton = container.querySelector('[data-role="retry"]');

    if (messageNode) {
      messageNode.textContent = message || i18n.cartGenericError || 'Unable to update your basket right now.';
    }

    cartPageRetryHandler = typeof retryHandler === 'function' ? retryHandler : null;
    if (retryButton) {
      retryButton.classList.toggle('hidden', !cartPageRetryHandler);
      retryButton.disabled = !cartPageRetryHandler;
    }

    container.classList.remove('hidden');
  }

  function clearCartPageError() {
    const container = document.getElementById('cart-page-error');
    if (!container) return;
    cartPageRetryHandler = null;
    container.classList.add('hidden');
  }

  function syncCartHeaderSummary(doc) {
    const currentSummary = document.getElementById('cart-item-count-summary');
    if (!currentSummary) return;

    const nextSummary = doc.getElementById('cart-item-count-summary');
    if (!nextSummary) {
      currentSummary.classList.add('hidden');
      currentSummary.textContent = '';
      return;
    }

    currentSummary.textContent = nextSummary.textContent;
    const shouldHide = nextSummary.classList.contains('hidden');
    currentSummary.classList.toggle('hidden', shouldHide);
  }

  function getFocusDescriptor(triggerButton) {
    if (!triggerButton) return null;
    const action = triggerButton.getAttribute('data-action');
    const line = triggerButton.getAttribute('data-line');
    const quantity = triggerButton.getAttribute('data-quantity');
    return {
      action: action || '',
      line: line || '',
      quantity: quantity || ''
    };
  }

  function restoreFocus(descriptor) {
    if (!descriptor) return;
    const baseSelector = `[data-action="${descriptor.action}"][data-line="${descriptor.line}"]`;
    let selector = baseSelector;
    if (descriptor.quantity) {
      selector += `[data-quantity="${descriptor.quantity}"]`;
    }
    let target = document.querySelector(selector);
    if (!target) {
      target = document.querySelector(baseSelector);
    }
    if (target) {
      target.focus({ preventScroll: true });
    }
  }

  function setLineControlsDisabled(lineNumber, disabled) {
    const controls = document.querySelectorAll(
      `[data-action="cart-quantity"][data-line="${lineNumber}"], [data-action="cart-remove"][data-line="${lineNumber}"]`
    );
    controls.forEach(control => {
      control.disabled = disabled;
      control.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    });
  }

  function refreshCartPageContent() {
    const container = document.getElementById('cart-page-content');
    if (!container) {
      return Promise.resolve();
    }

    const requestId = ++refreshRequestId;
    if (refreshController) {
      refreshController.abort();
    }
    refreshController = new AbortController();

    const refreshUrl = `${window.location.pathname}${window.location.search}`;
    return requestClient.fetchText(refreshUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: refreshController.signal,
      retry: 1,
      timeoutMs: 12000
    })
      .then(html => {
        clearCartPageError();
        if (requestId !== refreshRequestId) {
          return;
        }
        const container = document.getElementById('cart-page-content');
        if (!container) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const updatedContainer = doc.getElementById('cart-page-content');
        if (!updatedContainer) {
          // Unrecoverable markup mismatch fallback.
          window.location.assign(refreshUrl);
          return;
        }

        container.innerHTML = updatedContainer.innerHTML;
        syncCartHeaderSummary(doc);
        initializeCartNoteListener();

        if (window.cart && typeof window.cart.updateCartCount === 'function') {
          window.cart.updateCartCount();
        }
      })
      .catch(error => {
        if (error.kind === 'aborted' || error.name === 'AbortError') {
          return;
        }
        throw error;
      });
  }
  
  // Cart functionality
  function updateCartQuantity(lineNumber, newQuantity, triggerButton) {
    if (newQuantity < 1) {
      removeCartItem(lineNumber, triggerButton);
      return;
    }

    if (lineLocks.has(lineNumber)) {
      return;
    }
    lineLocks.add(lineNumber);
    setLineControlsDisabled(lineNumber, true);
    const focusDescriptor = getFocusDescriptor(triggerButton);
    
    // Get the current cart to preserve properties
    clearCartPageError();
    requestClient.fetchJSON('/cart.js', { retry: 1, timeoutMs: 10000 })
      .then(cart => {
        // Find the item by line number
        const item = cart.items[lineNumber];
        if (!item) {
          throw new Error(`Item not found in cart at line: ${lineNumber}`);
        }
        
        // Prepare the update with properties
        const updateData = {
          line: parseInt(lineNumber) + 1, // Shopify uses 1-based indexing
          quantity: newQuantity
        };
        
        // Add properties if they exist
        if (item.properties && Object.keys(item.properties).length > 0) {
          Object.keys(item.properties).forEach(key => {
            updateData[`properties[${key}]`] = item.properties[key];
          });
        }
        
        return requestClient.fetchJSON('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
          retry: 0,
          timeoutMs: 10000
        });
      })
      .then(() => refreshCartPageContent())
      .then(() => {
        restoreFocus(focusDescriptor);
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          return;
        }
        showCartPageError(i18n.cartQuantityError || 'Could not update item quantity. Please try again.', function() {
          updateCartQuantity(lineNumber, newQuantity, triggerButton);
        });
      })
      .finally(() => {
        lineLocks.delete(lineNumber);
        setLineControlsDisabled(lineNumber, false);
      });
  }
  
  function removeCartItem(lineNumber, triggerButton) {
    if (lineLocks.has(lineNumber)) {
      return;
    }
    lineLocks.add(lineNumber);
    setLineControlsDisabled(lineNumber, true);
    const focusDescriptor = getFocusDescriptor(triggerButton);

    // Disable the clicked button to prevent duplicate requests.
    const removeButton = triggerButton && triggerButton.closest('button');
    if (removeButton) {
      removeButton.disabled = true;
      removeButton.innerHTML = '<svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>';
    }
    
    clearCartPageError();
    requestClient.fetchJSON('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        line: parseInt(lineNumber) + 1, // Shopify uses 1-based indexing
        quantity: 0
      }),
      retry: 0,
      timeoutMs: 10000
    })
    .then(() => {
      return refreshCartPageContent();
    })
    .then(() => {
      restoreFocus(focusDescriptor);
    })
    .catch(error => {
      if (error.name === 'AbortError') {
        return;
      }
      showCartPageError(i18n.cartRemoveError || 'Could not remove item. Please try again.', function() {
        removeCartItem(lineNumber, triggerButton);
      });
      
      // Re-enable the button if there was an error
      if (removeButton) {
        removeButton.disabled = false;
        removeButton.innerHTML = '<svg class="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>';
      }
    })
    .finally(() => {
      lineLocks.delete(lineNumber);
      setLineControlsDisabled(lineNumber, false);
    });
  }

  // Cart Note functionality with debouncing
  let cartNoteTimeout;
  function initializeCartNoteListener() {
    const cartNoteTextarea = document.getElementById('cart-note');
    if (!cartNoteTextarea || cartNoteTextarea.dataset.initialized === 'true') return;

    cartNoteTextarea.dataset.initialized = 'true';
    cartNoteTextarea.addEventListener('input', function() {
      clearTimeout(cartNoteTimeout);
      cartNoteTimeout = setTimeout(() => {
        updateCartNote(this.value);
      }, 1000);
    });
  }

  function updateCartNote(note) {
    requestClient.fetchJSON('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        note: note
      }),
      retry: 0,
      timeoutMs: 10000
    })
    .then(() => {})
    .catch(error => {
      showCartPageError(i18n.cartNoteError || 'Could not update order note. Please try again.');
    });
  }
  
  // Event delegation for cart page controls (CSP-safe).
  document.addEventListener('click', function(e) {
    const target = e.target.closest('[data-action]');
    if (!target) return;

    const action = target.getAttribute('data-action');
    if (action === 'cart-quantity') {
      const line = target.getAttribute('data-line');
      const quantity = target.getAttribute('data-quantity');
      if (line !== null && quantity !== null) {
        updateCartQuantity(line, parseInt(quantity, 10), target);
      }
    }

    if (action === 'cart-remove') {
      const line = target.getAttribute('data-line');
      if (line !== null) {
        removeCartItem(line, target);
      }
    }
  });

  document.addEventListener('click', function(e) {
    const retryButton = e.target.closest('#cart-page-error [data-role="retry"]');
    if (!retryButton || typeof cartPageRetryHandler !== 'function') return;
    e.preventDefault();
    cartPageRetryHandler();
  });

  // Keep a minimal legacy API for backwards compatibility.
  window.updateCartQuantity = function(lineNumber, newQuantity) {
    updateCartQuantity(lineNumber, newQuantity, null);
  };
  window.removeCartItem = function(lineNumber) {
    removeCartItem(lineNumber, null);
  };
  window.updateCartNote = updateCartNote;
  initializeCartNoteListener();
})();
